// features/ups/modals/AddAccountModal.js
// Click-to-open modal to create an account for a specific agency (role, username, email, password) and submit via createAccount, then close on success.
import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { createAccount } from '../../agencyAccounts/agencyAccountsSlice';

const inputClass =
  'w-full rounded-xl border border-app-border bg-app-card px-4 py-3 text-sm text-app-text outline-none transition placeholder:text-app-muted focus:border-app-primary focus:ring-2 focus:ring-app-primary/20';
const labelClass = 'mb-1 block text-sm font-medium text-app-muted';
const primaryButtonClass =
  'rounded-xl bg-app-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60';
const secondaryButtonClass =
  'rounded-xl border border-app-border bg-app-card px-4 py-2.5 text-sm font-semibold text-app-text transition hover:bg-app-surface disabled:cursor-not-allowed disabled:opacity-60';

export default function AddAccountModal({ isOpen, onClose, agency }) {
  const dispatch = useDispatch();
  const roleId = 'add-account-role';
  const usernameId = 'add-account-username';
  const emailId = 'add-account-email';
  const passwordId = 'add-account-password';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [role, setRole] = useState('agency_user');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const canCreate = username.trim() && email.trim() && password;

  const close = () => {
    if (busy) return;
    onClose();
  };

  const handleCreate = async () => {
    if (!canCreate || busy) return;
    setBusy(true);
    setError('');
    try {
      await dispatch(createAccount({
        agencyId: agency.id, role, username: username.trim(), email: email.trim(), password
      })).unwrap();
      onClose();
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Failed to create account');
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/45 p-0 backdrop-blur-sm sm:items-center sm:p-4" onClick={close}>
      <div className="flex h-[78dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-app-border bg-app-card text-app-text shadow-2xl sm:h-auto sm:max-h-[92dvh] sm:max-w-2xl sm:rounded-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-app-border px-4 py-4 sm:px-5">
          <h3 className="text-lg font-semibold">Add account — {agency?.name}</h3>
          <button
            type="button"
            onClick={close}
            disabled={busy}
            aria-label="Close add account modal"
            className="rounded-xl p-2 text-app-muted transition hover:bg-app-surface hover:text-app-text disabled:cursor-not-allowed disabled:opacity-60"
          >
            <X size={18} />
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
          <div>
            <label htmlFor={roleId} className={labelClass}>Role</label>
            <select id={roleId} name="role" className={inputClass} value={role} onChange={e => setRole(e.target.value)} disabled={busy}>
              <option value="agency_admin">Agency Admin</option>
              <option value="agency_user">Agency User</option>
            </select>
          </div>
          <div>
            <label htmlFor={usernameId} className={labelClass}>Username</label>
            <input id={usernameId} name="username" className={inputClass} value={username} onChange={e => setUsername(e.target.value)} disabled={busy} />
          </div>
          <div>
            <label htmlFor={emailId} className={labelClass}>Email</label>
            <input id={emailId} name="email" type="email" className={inputClass} value={email} onChange={e => setEmail(e.target.value)} disabled={busy} />
          </div>
          <div>
            <label htmlFor={passwordId} className={labelClass}>Password</label>
            <input id={passwordId} name="password" type="password" className={inputClass} value={password} onChange={e => setPassword(e.target.value)} disabled={busy} />
          </div>
          {error && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
        </div>
        <div
          className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-app-border bg-app-surface px-4 py-3 sm:px-5 sm:py-4"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
        >
          <button type="button" onClick={close} disabled={busy} className={secondaryButtonClass}>Cancel</button>
          <button type="button" disabled={!canCreate || busy} onClick={handleCreate} className={primaryButtonClass}>
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
